import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import { tokens } from "../theme";

/** Slides the drawer away first, then runs `then` (default: `onClose`). */
export type Dismiss = (then?: () => void) => void;

/** Footer content, or a render function that gets {@link Dismiss}. */
type Actions = ReactNode | ((dismiss: Dismiss) => ReactNode);

/**
 * A soft "settle" curve: quick start, long gentle landing. Longer than MUI's
 * 225ms default, which reads as a snap on a 640px-wide panel.
 */
const SLIDE = {
  duration: { enter: 380, exit: 260 },
  easing: {
    enter: "cubic-bezier(0.32, 0.72, 0, 1)",
    exit: "cubic-bezier(0.32, 0.72, 0, 1)",
  },
};

/**
 * Lets a surface that the PAGE mounts already-open still animate both ways.
 *
 * MUI skips the enter transition when a Drawer mounts with `open` already
 * true (it pops in), and a page that unmounts the surface on close kills the
 * exit transition (it vanishes). So: start closed and open on the next
 * commit; and on close, play the exit first and only then call the page's
 * `onClose`, which unmounts it.
 */
function useAnimatedOpen(open: boolean, onClose: () => void) {
  const [shown, setShown] = useState(false);
  const after = useRef<(() => void) | null>(null);
  useEffect(() => {
    setShown(open);
  }, [open]);
  const dismiss = useCallback<Dismiss>(
    (then) => {
      after.current = then ?? onClose;
      setShown(false);
    },
    [onClose]
  );
  const onExited = useCallback(() => {
    const fn = after.current;
    after.current = null;
    fn?.();
  }, []);
  return { shown, dismiss, onExited };
}

const renderActions = (actions: Actions, dismiss: Dismiss) =>
  typeof actions === "function" ? actions(dismiss) : actions;

/**
 * The CMS's one rule for item forms: CREATING a thing opens a modal, EDITING
 * an existing thing opens a drawer from the right.
 *
 * `EditDrawer` is that right-hand drawer - the same shell as the Start view's
 * MenuItemDrawer (NendaPortal_1 1677:13245): title + optional subtitle and a
 * close X on top, a scrolling body, and a pinned footer for the actions.
 */
export interface EditDrawerProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  subtitle?: ReactNode;
  /**
   * Footer, usually Cancel + Save, pinned below the scrolling body. Pass a
   * function to get `dismiss`: Cancel → `dismiss()`, a successful save →
   * `dismiss(onSaved)`, so the drawer slides out before the page reacts.
   */
  actions?: Actions;
  children: ReactNode;
  /** Optional icon, shown in a tinted tile left of the title. */
  icon?: ReactNode;
  /** Right side of the header, before the X (a status chip, an id). */
  headerAside?: ReactNode;
  /** Notices pinned above the footer buttons (validation, save errors). */
  footerTop?: ReactNode;
  /** A Tabs row under the title; it replaces the header divider. */
  tabs?: ReactNode;
  /** Body background, e.g. a grey wash behind white cards. */
  bodyBg?: string;
  /** Desktop width in px. Phones always get the full width. */
  width?: number;
  /** Blocks closing via backdrop / Escape / X, e.g. while saving. */
  busy?: boolean;
  "data-testid"?: string;
}

/** The small brand-tinted square some editors lead their header with. */
function IconTile({ icon }: { icon: ReactNode }) {
  return (
    <Box
      aria-hidden
      sx={{
        width: 44,
        height: 44,
        flexShrink: 0,
        borderRadius: "10px",
        bgcolor: tokens.brandTintSoft,
        color: tokens.brand,
        display: "grid",
        placeItems: "center",
      }}
    >
      {icon}
    </Box>
  );
}

export function EditDrawer({
  open,
  onClose,
  title,
  subtitle,
  icon,
  headerAside,
  footerTop,
  tabs,
  bodyBg,
  actions,
  children,
  width = 640,
  busy = false,
  "data-testid": testId,
}: EditDrawerProps) {
  const { t } = useTranslation();
  const titleId = useId();
  const { shown, dismiss, onExited } = useAnimatedOpen(open, onClose);
  const close = busy ? undefined : () => dismiss();
  return (
    <Drawer
      anchor="right"
      open={shown}
      onClose={close}
      transitionDuration={SLIDE.duration}
      SlideProps={{ easing: SLIDE.easing, onExited }}
      // Modal level, not MUI's drawer level (below modals): an editor opened
      // from inside a dialog (the Review queue) must land ON it. Equal levels
      // stack by mount order, so whatever opens later is on top.
      sx={{ zIndex: (theme) => theme.zIndex.modal }}
      PaperProps={{
        role: "dialog",
        "aria-modal": true,
        "aria-labelledby": titleId,
        sx: {
          width: { xs: "100%", md: width },
          maxWidth: "100vw",
          borderRadius: { xs: 0, md: "16px 0 0 16px" },
          display: "flex",
          flexDirection: "column",
        },
      }}
      data-testid={testId}
    >
      <Box
        sx={{
          px: { xs: 2.5, md: "33px" },
          pt: { xs: 2.5, md: "28px" },
        }}
      >
        <Stack direction="row" spacing={2} alignItems="flex-start">
          {icon && <IconTile icon={icon} />}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              id={titleId}
              component="h2"
              sx={{
                typography: "headline",
                fontWeight: "fontWeightSemibold",
                lineHeight: "normal",
                color: tokens.ink,
              }}
            >
              {title}
            </Typography>
            {subtitle && (
              <Typography
                variant="body2"
                sx={{
                  mt: "6px",
                  lineHeight: "20px",
                  color: tokens.textSecondary,
                }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
          {headerAside}
          <IconButton
            aria-label={t("buttons.close")}
            onClick={close}
            disabled={busy}
            sx={{ mt: "-4px", color: tokens.textSecondary }}
          >
            <CloseIcon />
          </IconButton>
        </Stack>
        {tabs ? (
          <Box
            sx={{
              mt: 1.5,
              mx: { xs: -2.5, md: "-33px" },
              px: { xs: 1, md: "21px" },
              borderBottom: `1px solid ${tokens.line}`,
            }}
          >
            {tabs}
          </Box>
        ) : (
          <Divider sx={{ mt: "20px", borderColor: tokens.line }} />
        )}
      </Box>

      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          px: { xs: 2.5, md: "33px" },
          py: 3,
          bgcolor: bodyBg,
        }}
      >
        {children}
      </Box>

      {(actions || footerTop) && (
        <Box
          sx={{
            px: { xs: 2.5, md: "33px" },
            py: 2,
            borderTop: `1px solid ${tokens.line}`,
            bgcolor: tokens.surface,
          }}
        >
          {footerTop}
          <Stack direction="row" justifyContent="flex-end" spacing={1}>
            {renderActions(actions, dismiss)}
          </Stack>
        </Box>
      )}
    </Drawer>
  );
}

/**
 * For a form component shared by "New X" and "Edit X": a modal while creating,
 * the right-hand {@link EditDrawer} while editing. Same body, same actions.
 */
export function ItemFormSurface({
  mode,
  open = true,
  onClose,
  title,
  subtitle,
  icon,
  headerAside,
  footerTop,
  actions,
  children,
  maxWidth = "sm",
  drawerWidth,
  busy,
  "data-testid": testId,
}: {
  mode: "create" | "edit";
  open?: boolean;
  onClose: () => void;
  title: ReactNode;
  subtitle?: ReactNode;
  icon?: ReactNode;
  headerAside?: ReactNode;
  footerTop?: ReactNode;
  actions?: Actions;
  children: ReactNode;
  /** Modal width (create). */
  maxWidth?: "xs" | "sm" | "md" | "lg" | "xl";
  /** Drawer width in px (edit). */
  drawerWidth?: number;
  busy?: boolean;
  "data-testid"?: string;
}) {
  // Called unconditionally (rules of hooks); only the modal branch uses it.
  const modal = useAnimatedOpen(open, onClose);
  if (mode === "edit") {
    return (
      <EditDrawer
        open={open}
        onClose={onClose}
        title={title}
        subtitle={subtitle}
        icon={icon}
        headerAside={headerAside}
        footerTop={footerTop}
        actions={actions}
        width={drawerWidth}
        busy={busy}
        data-testid={testId}
      >
        {children}
      </EditDrawer>
    );
  }
  return (
    <Dialog
      open={modal.shown}
      onClose={busy ? undefined : () => modal.dismiss()}
      TransitionProps={{ onExited: modal.onExited }}
      maxWidth={maxWidth}
      fullWidth
      data-testid={testId}
    >
      <DialogTitle
        component="div"
        sx={{ pb: 1, display: "flex", gap: 2, alignItems: "flex-start" }}
      >
        {icon && <IconTile icon={icon} />}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            component="h2"
            variant="h6"
            sx={{ fontWeight: "fontWeightSemibold" }}
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="body2" sx={{ color: tokens.textSecondary }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        {headerAside}
      </DialogTitle>
      <DialogContent>{children}</DialogContent>
      {footerTop && <Box sx={{ px: 3, pt: 1 }}>{footerTop}</Box>}
      {actions && (
        <DialogActions>{renderActions(actions, modal.dismiss)}</DialogActions>
      )}
    </Dialog>
  );
}
